import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useArenaContextFromLocationState } from "./SetUpTwoFA/hooks";
import { getBaseUrl } from "./utils";

export enum ActivationRedirectDestination {
  VerifyTwoFA = "join/verifyTwoFA",
  LegacyTwoFA = "link/verifyTwoFA",
  UpgradeTwoFaSetup = "upgrade/setupTwoFA",
  UpgradeTwoFaVerify = "upgrade/verifyTwoFA",
  ShowRecoveryCodes = "recovery",
  NewManagers = "newManagers",
  NewManagerInvitations = "newInvitations",
  EditLPProfile = "editProfile",
  Final = "",
}

/**
 * A custom hook to redirect to a specific part of the activation flow.
 * Destination can either be provided during instantiation, or during the callback call of the hook.
 * @returns A callback to push the redirect to history with location state.
 * @borrows previous implementation from `src/apps/portal/Portal/common/AccountActivation/utils.ts`
 */
export const useActivationRedirect = <LocationState>({
  destination,
  addArenaContextToLocationState = false,
}: {
  destination?: ActivationRedirectDestination;
  addArenaContextToLocationState?: boolean;
} = {}) => {
  const history = useHistory();
  const location = useLocation<Record<string, unknown>>();
  const shouldRefetchQueries = Boolean(location.state?.shouldRefetchQueries);

  const { arenaName, logoUrl } = useArenaContextFromLocationState();

  const initializedDestination = destination;

  const baseUrl = getBaseUrl(location.pathname);
  const searchParams = new URLSearchParams(location.search);
  const path = searchParams.get("path") || "home";

  const redirectFn = useCallback(
    ({
      state,
      destination,
      multiManagerOverride,
    }: {
      state?: LocationState;
      destination?: ActivationRedirectDestination;
      multiManagerOverride?: boolean;
    } = {}) => {
      const resolvedDestination = destination || initializedDestination;
      if (resolvedDestination === undefined) {
        throw new Error("No destination provided to `useActivationRedirect` hook.");
      }

      let queryParams = `?path=${path}`;
      if (resolvedDestination === ActivationRedirectDestination.Final) {
        queryParams = queryParams.replace("?path=", "");
      }

      const finalBaseUrl = multiManagerOverride ? "/lp" : baseUrl;
      const redirectUrl = `${finalBaseUrl}/${resolvedDestination}${queryParams}`;

      history.push(
        redirectUrl,
        state || addArenaContextToLocationState || shouldRefetchQueries
          ? {
              ...state,
              ...(addArenaContextToLocationState && { arenaName, logoUrl }),
              ...(shouldRefetchQueries && { shouldRefetchQueries }),
            }
          : undefined,
      );
    },
    [
      initializedDestination,
      path,
      baseUrl,
      history,
      addArenaContextToLocationState,
      arenaName,
      logoUrl,
      shouldRefetchQueries,
    ],
  );

  return redirectFn;
};
